import AdminHeader from "components/Headers/AdminHeader";
import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  UncontrolledTooltip,
  Input,
  Badge,
  CardTitle,
} from "reactstrap";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE, PAYMENT_OPTIONS, PAYMENT_STATUS } from "components/constant";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { formatDate } from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import DatePicker from "react-datepicker";
import PaymentApprovalModal from "./Paymentapprovalmodal";
import { useDispatch, useSelector } from "react-redux";
import { getPaymentList } from "action/payment";
import { paymentList } from "constants/payment";

const PaymentList = () => {
  const { notify } = useContext(NotificationContext);
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const paymentListData = useSelector(
    (state) => state.payment.paymentList,
  );

  const [pageNum, setPageNum] = useState(1);

  // Local mirror of the fetched list - lets Approve/Reject optimistically
  // update a row without waiting on a refetch.
  const [paymentData, setPaymentData] = useState([]);

  const [approvalModal, setApprovalModal] = useState({ isOpen: false, payment: null });
  const [actionLoader, setActionLoader] = useState(null);

  const [searchState, setSearchState] = useState({
    name: "",
    courseName: "",
    paymentMode: null,
    paymentStatus: null,
    requestedOn: [null, null],
    filterParams: [],
  });

  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  // Fetch payments whenever the page or filters change
  useEffect(() => {
    getPaymentList(dispatch, pageNum, searchState.filterParams, {
      sortBy: "CreatedAt",
      sortType: "desc",
    },);

    return () => {
      dispatch({ type: paymentList, payload: {} });
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  // Sync the redux response into local state + derive loading/error/empty flags
  useEffect(() => {
    if (paymentListData?.code === 200) {
      const list = paymentListData?.data?.CourseEnrollmentPayments || [];

      setPaymentData(list);
      setListState({
        isLoading: false,
        error: "",
        noRecords: list.length === 0,
        showLoaderOnClick: false,
      });
    } else if (paymentListData?.code || paymentListData?.message) {
      setListState({
        isLoading: false,
        error: paymentListData?.message || "Failed to load payments",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [paymentListData]);

  // Custom Option renderer for payment mode select
  const paymentModeOption = (props) => {
    // const isOnline = props.data.value === "ONLINE";
    return (
      <components.Option {...props}>
        <div className="d-flex align-items-center">
          <span>
            {props.data.label}
          </span>
        </div>
      </components.Option>
    );
  };

  const paymentModeSingleValue = (props) => {
    // const isOnline = props.data.value === "ONLINE";
    return (
      <components.SingleValue {...props}>
        <div className="d-flex align-items-center">
          <span>
            {props.data.label}
          </span>
        </div>
      </components.SingleValue>
    );
  };

  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge color={props.data.value === "CAPTURED" ? "success" : props.data.value === "FAILED" ? "danger" : "secondary"} pill>
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge color={props.data.value === "CAPTURED" ? "success" : props.data.value === "FAILED" ? "danger" : "secondary"} pill>
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  // Builds the {field, condition, filterValues} payload the API expects,
  // mirroring the QuestionBankList pattern. Field names below are inferred
  // from the response shape (PaidByName, CourseName, PaymentType,
  // PaymentStatus, RequestedDate) - confirm these against the backend.
  const buildFilters = () => {
    const filters = [];

    if (searchState.name?.trim()) {
      filters.push({
        field: "PaidByName",
        condition: "like",
        filterValues: [searchState.name.trim()],
      });
    }

    if (searchState.courseName?.trim()) {
      filters.push({
        field: "CourseName",
        condition: "like",
        filterValues: [searchState.courseName.trim()],
      });
    }

    if (searchState.paymentMode?.value) {
      filters.push({
        field: "PaymentMode",
        condition: "eq",
        filterValues: [searchState.paymentMode.value],
      });
    }

    if (searchState.paymentStatus?.value) {
      filters.push({
        field: "PaymentStatus",
        condition: "eq",
        filterValues: [searchState.paymentStatus.value],
      });
    }

    if (searchState.requestedOn[0]) {
      if (searchState.requestedOn[1]) {
        filters.push({
          field: "PaymentDate",
          condition: "btw",
          filterValues: [
            formatDate(searchState.requestedOn[0]),
            formatDate(searchState.requestedOn[1]),
          ],
        });
      } else {
        filters.push({
          field: "PaymentDate",
          condition: "eq",
          filterValues: [formatDate(searchState.requestedOn[0])],
        });
      }
    }

    return filters.length ? JSON.stringify(filters) : [];
  };

  const handleSearch = () => {
    const newFilters = buildFilters();

    if (
      JSON.stringify(searchState.filterParams) !== JSON.stringify(newFilters)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilters,
      }));

      if (pageNum !== 1) {
        setPageNum(1);
      }
    }
  };

  const handleClear = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      name: "",
      courseName: "",
      paymentMode: null,
      paymentStatus: null,
      requestedOn: [null, null],
      filterParams: [],
    });

    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // Handle Approve - Updated to accept paymentDate
  const handleApprove = (remark, paymentDate) => {
    setActionLoader("approve");

    setTimeout(() => {
      setActionLoader(null);
      if (approvalModal.payment) {
        const formattedDate = formatDate(paymentDate || new Date());

        // Optimistically update the local list. Status strings match the
        // API contract (CAPTURED / FAILED / PENDING), not the old mock's
        // CONFIRMED / REJECTED, so the existing status badges color correctly.
        setPaymentData((prev) =>
          prev.map((item) =>
            item.id === approvalModal.payment.id
              ? {
                ...item,
                paymentStatus: "CAPTURED",
                paymentDate: formattedDate,
              }
              : item
          )
        );
      }
      setApprovalModal({ isOpen: false, payment: null });
      notify("success", "Payment confirmed successfully");
    }, 500);
  };

  // Handle Reject
  const handleReject = (remark) => {
    setActionLoader("reject");

    setTimeout(() => {
      setActionLoader(null);
      if (approvalModal.payment) {
        setPaymentData((prev) =>
          prev.map((item) =>
            item.id === approvalModal.payment.id
              ? {
                ...item,
                paymentStatus: "FAILED",
              }
              : item
          )
        );
      }
      setApprovalModal({ isOpen: false, payment: null });
      notify("danger", "Payment rejected");
    }, 500);
  };

  // Handle Confirm Payment - Open Modal
  // const handleConfirmPayment = (payment) => {
  //   setApprovalModal({
  //     isOpen: true,
  //     payment: {
  //       ...payment,
  //       totalAmount: payment.totalAmount,
  //       totalGst: payment.totalGst,
  //       payableAmount: payment.payableAmount,
  //     }
  //   });
  // };

  // Map API fields -> the prop names the columns below already expect,
  // so the column/Cell definitions themselves don't need to change.
  const formattedData = paymentData.map((item, index) => ({
    id: item.id,
    payerName: item.paidByName,
    courseName: item.courseName,
    batchName: item.batchName,
    paymentEntryDate: item.requestedDate,
    paymentDate: item.paymentDate,
    totalAmount: item.totalAmount,
    totalGst: item.totalGst,
    payableAmount: item.payableAmount,
    paidAmount: item.paymentStatus === "CAPTURED" ? item.payableAmount : 0,
    enrolledTraineesCount: item.traineeCount,
    paymentmode: item.paymentmode,
    paymentType: item.paymentType,
    paymentStatus: item.paymentStatus,
    paymentRemark: item.paymentRemark,
    srNo:
      (pageNum - 1) *
      (paymentListData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
  }));

  const totalPages =
    Math.ceil(
      (paymentListData?.data?.totalFilteredRecords || 0) /
      (paymentListData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE)
    ) || 1;

  const columns = [
    {
      Header: "Sr No",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 60,
      maxWidth: 60,
    },
    {
      Header: "Payer name",
      accessor: "payerName",
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Course & Batch",
      accessor: "courseName",
      minWidth: 200,
      maxWidth: 250,
      Cell: ({ row }) => (
        <div className="text-wrapper d-flex flex-column">
          <div className="font-weight-medium">{row.original.courseName}</div>
          <div className="text-muted">
            <i className="fas fa-layer-group mr-1 payment-list-column-icon" />
            {row.original.batchName}
          </div>
        </div>
      ),
    },
    {
      Header: "Payment Date",
      accessor: "paymentEntryDate",
      minWidth: 180,
      maxWidth: 200,
      Cell: ({ row }) => (
        <span className="font-weight-medium">{row.original.paymentDate || "-"}</span>
      ),
    },
    {
      Header: "Paid Amount",
      accessor: "totalAmount",
      rightAlign: true,
      minWidth: 150,
      maxWidth: 180,
      Cell: ({ row }) => {
        const { payableAmount } = row.original;  //const { totalAmount, totalGst, payableAmount, paidAmount } = row.original;
        return (
          <span className="font-weight-bold text-deep-blue">&#8377;{payableAmount.toLocaleString("en-IN")}</span>
        );
      },
    },
    {
      Header: "Enrollment Details",
      accessor: "enrollmentDetails",
      minWidth: 160,
      maxWidth: 200,
      centerAlign: true,
      Cell: ({ row }) => (
        <div className="d-flex flex-column align-items-center justify-content-center">
          <span>
            {row.original.paymentType === "SINGLE" ? "INDIVIDUAL" : row.original.paymentType || "-"}
          </span>
          {row.original.paymentType === "BULK" && <div className="text-muted small">
            <i className="fas fa-user-group mr-1 payment-list-column-icon" />
            <span>Trainees:</span>{" "}
            {row.original.enrolledTraineesCount ?? 0}
          </div>}

        </div>
      ),
    },
    {
      Header: "Payment Mode",
      accessor: "paymentmode",
      minWidth: 120,
      maxWidth: 140,
      centerAlign: true,
      Cell: ({ value }) => {
        // const isOnline = value === "ONLINE";
        return (
          <div className="d-flex align-items-center justify-content-center">
            <span>
              {value === "" ? "-" : value}
            </span>
          </div>
        );
      },
    },
    {
      Header: "Payment Status",
      accessor: "paymentStatus",
      minWidth: 145,
      maxWidth: 145,
      centerAlign: true,
      Cell: ({ value }) => {

        return (
          <Badge color={value === "CAPTURED" ? "success" : value === "FAILED" ? "danger" : "warning"} pill>
            {value === "CAPTURED" ? "APPROVED" : value === "PENDING" ? "PENDING FOR APPROVAL" : value === "FAILED" ? "REJECTED" : value}
          </Badge>
        );
      },
    },
    {
      Header: "Actions",
      disableSortBy: true,
      minWidth: 90,
      maxWidth: 90,
      centerAlign: true,
      Cell: ({ row }) => {
        const { id } = row.original; // const { id, paymentDate } = row.original;

        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <Button
              className="btn-link p-0"
              id={`view${id}`}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                const encodedId = urlEncodedId(id);
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/payments/${encodedId}/details`
                    : `/admin/payments/${encodedId}/details`,
                );
              }}
            >
              <i className="fas fa-file-invoice" />
            </Button>
            <UncontrolledTooltip delay={0} target={`view${id}`}>
              View
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const handleOnRowClick = (rowData) => {
    try {
      const encodedId = urlEncodedId(rowData.id);
      navigate(
        role === "SUPER_ADMIN"
          ? `/super-admin/payments/${encodedId}/details`
          : `/admin/payments/${encodedId}/details`,
      );
    } catch (error) {

    }
  };

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6">
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h3" className="mb-0 text-default">Payments</CardTitle>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by payer name"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.name}
                      onKeyDown={handleKeyDown}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                    />

                    <Input
                      type="text"
                      placeholder="Search by course name"
                      className="mr-2 mb-2 filter-input-large"
                      value={searchState.courseName}
                      onKeyDown={handleKeyDown}
                      onChange={(e) =>
                        setSearchState((prev) => ({
                          ...prev,
                          courseName: e.target.value,
                        }))
                      }
                    />

                    <div className="d-flex align-items-center mr-2 mb-2 mt-2">
                      <DatePicker
                        onChangeRaw={(e) => e.preventDefault()}
                        selected={searchState.requestedOn[0]}
                        onChange={(dates) =>
                          setSearchState((prev) => ({
                            ...prev,
                            requestedOn: dates,
                          }))
                        }
                        startDate={searchState.requestedOn[0]}
                        endDate={searchState.requestedOn[1]}
                        selectsRange
                        onKeyDown={handleKeyDown}
                        isClearable={!!searchState.requestedOn[0]}
                        placeholderText="Select payment date or range"
                        className="form-control mb-2 filter-input-large"
                        dateFormat="dd-MMM-yyyy"
                        showMonthDropdown
                        showYearDropdown
                        scrollableYearDropdown
                        calendarClassName="custom-calendar"
                        yearDropdownItemNumber={100}
                      />
                    </div>

                    <Select
                      className="mr-2 mb-2 filter-select"
                      styles={fixedWidthCustomStyle()}
                      options={PAYMENT_OPTIONS}
                      onKeyDown={handleKeyDown}
                      components={{
                        Option: paymentModeOption,
                        SingleValue: paymentModeSingleValue
                      }}
                      placeholder={"Select mode of payment"}
                      value={searchState.paymentMode}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          paymentMode: opt,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      styles={fixedWidthCustomStyle()}
                      options={PAYMENT_STATUS}
                      onKeyDown={handleKeyDown}
                      components={{ Option, SingleValue }}
                      placeholder="Select status"
                      value={searchState.paymentStatus}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          paymentStatus: opt,
                        }))
                      }
                    />

                    <Button
                      color="default"
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.showLoaderOnClick ? "search-btn" : ""}`}
                      onClick={handleSearch}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {searchState.filterParams.length > 0 && (
                      <Button
                        className="mb-2 btn-deep-blue filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {paymentListData?.data?.totalRecords > 0 ? (
                    <>
                      {searchState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing {paymentListData?.data?.totalFilteredRecords} of {paymentListData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {paymentListData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                <div className="table-responsive">
                  {listState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : listState.error ? (
                    <div className="loaderContainer error-style">
                      <div className="error-icon">
                        <i class="fa-solid fa-circle-exclamation"></i>
                      </div>
                      <span>{listState.error}</span>
                    </div>
                  ) : listState.noRecords ? (
                    <div className="loaderContainer empty-style">
                      <div className="empty-icon">
                        <i className="fa-solid fa-circle-info"></i>
                      </div>
                      <span>No payments found.</span>
                    </div>
                  ) : (
                    <CommonTable
                      columns={columns}
                      data={formattedData}
                      activePage={pageNum}
                      setPageNum={setPageNum}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      showPaginationBottom
                      totalPages={totalPages}
                      rowClick={handleOnRowClick}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <PaymentApprovalModal
        isOpen={approvalModal.isOpen}
        toggleModal={() => setApprovalModal({ isOpen: false, payment: null })}
        payment={approvalModal.payment}
        onApprove={handleApprove}
        onReject={handleReject}
        loaderOnClick={actionLoader}
      />
    </>
  );
};

export default PaymentList;